import InsuranceAvatar from 'assets/images/insurance-avatar.png';
import { useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import { formatIban, formatTransactionAmount, normalizeIban } from 'common/utils/formatUtils';
import { format } from 'date-fns';
import {
  FinancialProductType,
  InsuringPersonType,
  MotorLiabilityInsuranceProductFragment,
  useMotorLiabilityInsuranceProductQuery,
  useUpdateMotorLiabilityInsuranceProductMutation,
  VehicleType,
} from 'generated/graphql';
import { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProductDetail } from '../ProductDetail/ProductDetail';
import { QRCodeDialog, QRCodeDialogProps } from '../QRCodeDialog/QRCodeDialog';
import { QRCodeMissingDataDialog, QRCodeMissingDataDialogProps } from '../QRCodeDialog/QRCodeMissingDataDialog';
import { ButtonRowType, DetailRowType, Section } from '../types';
import { ProductDetailDialogProps } from '../useProductDetail';
import { mapMotorLiabilityInsuranceProductToInput } from './utils';

export const MotorLiabilityInsuranceProductDetail: React.FC<ProductDetailDialogProps> = ({ id, ...props }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();

  const { data } = useMotorLiabilityInsuranceProductQuery({
    variables: {
      id,
    },
  });

  const qrCodeDialog = useDialog<QRCodeDialogProps, void>(QRCodeDialog, {
    instanceId: 'qr-code-dialog',
    tmpNavigable: true,
  });

  const qrCodeMissingDataDialog = useDialog<QRCodeMissingDataDialogProps, void>(QRCodeMissingDataDialog, {
    instanceId: 'qr-code-missing-data-dialog',
  });

  const handleQRPayment = () => {
    const iban = data?.detail.paymentInfo?.ibanForPayment;
    const amount = data?.detail.paymentPeriod?.expectedAmount?.amount;
    if (iban && amount) {
      qrCodeDialog.create({
        amount,
        constantSymbol: data?.detail.paymentInfo?.constantSymbol ?? undefined,
        currency: data?.detail.paymentPeriod?.expectedAmount?.currency ?? 'EUR',
        iban,
        specificSymbol: data?.detail.paymentInfo?.specificSymbol ?? undefined,
        variableSymbol: data?.detail.paymentInfo?.variableSymbol ?? undefined,
      });
    } else {
      qrCodeMissingDataDialog.create({});
    }
  };

  const periodicityOptions = usePeriodicityOptions();
  const vehicleTypeOptions = [
    {
      label: t('products:motorLiabilityInsurance.detail.vehicleInfo.type.automobile.label'),
      value: VehicleType.Automobile,
    },
    {
      label: t('products:motorLiabilityInsurance.detail.vehicleInfo.type.motorcycle.label'),
      value: VehicleType.Motorcycle,
    },
    {
      label: t('products:motorLiabilityInsurance.detail.vehicleInfo.type.other.label'),
      value: VehicleType.Other,
    },
  ];
  const insuringPersonTypeOptions = [
    {
      label: t('products:motorLiabilityInsurance.detail.insuringPerson.type.natural.label'),
      value: InsuringPersonType.Natural,
    },
    {
      label: t('products:motorLiabilityInsurance.detail.insuringPerson.type.legal.label'),
      value: InsuringPersonType.Legal,
    },
  ];

  const content: Section<MotorLiabilityInsuranceProductFragment, Path<MotorLiabilityInsuranceProductFragment>>[] = [
    {
      rows: [
        {
          inputType: 'text',
          label: t('products:motorLiabilityInsurance.detail.institutionName'),
          path: 'institutionName',
          skip: !!data?.detail.institution?.id,
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'institutionName'>,
        {
          inputType: 'text',
          label: t('products:motorLiabilityInsurance.detail.productName'),
          path: 'name',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'name'>,
        {
          inputType: 'text',
          label: t('products:motorLiabilityInsurance.detail.contractNumber'),
          path: 'contractNumber',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'contractNumber'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          formatter: (value: number) =>
            formatTransactionAmount(value, i18n.language, 2, data?.detail.paymentPeriod?.expectedAmount?.currency),
          inputEndAdornment: data?.detail.paymentPeriod?.expectedAmount?.currency,
          inputType: 'amount',
          label: t('products:motorLiabilityInsurance.detail.instalment'),
          // we are storing paymentPeriod.expectedAmount.amount as negative number
          // but we want user to type a positive equivalent
          parse: (value) => -value,
          path: 'paymentPeriod.expectedAmount.amount',
          transform: (value) => -value,
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'paymentPeriod.expectedAmount.amount'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:motorLiabilityInsurance.detail.nextPayment'),
          path: 'paymentPeriod.expectedDate',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'paymentPeriod.expectedDate'>,
        {
          formatter: (value) =>
            periodicityOptions.find(
              (option) =>
                option.value.periodCount === value.periodCount && option.value.periodTypeCode === value.periodTypeCode,
            )?.label ?? '',
          inputType: 'radioSelect',
          label: t('products:motorLiabilityInsurance.detail.periodType'),
          options: periodicityOptions,
          parse: (value) => ({
            expectedAmount: data?.detail.paymentPeriod?.expectedAmount,
            expectedDate: data?.detail.paymentPeriod?.expectedDate,
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
          path: 'paymentPeriod',
          transform: (value) => ({
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'paymentPeriod'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:motorLiabilityInsurance.detail.contractEndDate'),
          path: 'contractEndDate',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'contractEndDate'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          inputType: 'variableSymbol',
          label: t('products:motorLiabilityInsurance.detail.variableSymbol'),
          path: 'paymentInfo.variableSymbol',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'paymentInfo.variableSymbol'>,
        {
          inputType: 'specificSymbol',
          label: t('products:motorLiabilityInsurance.detail.specificSymbol'),
          path: 'paymentInfo.specificSymbol',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'paymentInfo.specificSymbol'>,
        {
          inputType: 'constantSymbol',
          label: t('products:motorLiabilityInsurance.detail.constantSymbol'),
          path: 'paymentInfo.constantSymbol',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'paymentInfo.constantSymbol'>,
        {
          formatter: formatIban,
          inputType: 'iban',
          label: t('products:motorLiabilityInsurance.detail.ibanForPayment'),
          parse: normalizeIban,
          path: 'paymentInfo.ibanForPayment',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'paymentInfo.ibanForPayment'>,
        {
          color: 'blue.dark',
          label: t('products:motorLiabilityInsurance.detail.qrCode'),
          onClick: handleQRPayment,
        } as ButtonRowType,
      ],
      title: t('products:motorLiabilityInsurance.detail.paymentInfo.title'),
    },
    {
      rows: [
        {
          formatter: (value) => vehicleTypeOptions.find((option) => option.value === value)?.label ?? '',
          inputType: 'radioSelect',
          label: t('products:motorLiabilityInsurance.detail.vehicleInfo.type.title'),
          options: vehicleTypeOptions,
          path: 'vehicleInfo.type',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'vehicleInfo.type'>,
        {
          inputType: 'text',
          label: t('products:motorLiabilityInsurance.detail.vehicleInfo.licenseNumber'),
          path: 'vehicleInfo.licenseNumber',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'vehicleInfo.licenseNumber'>,
      ],
      title: t('products:motorLiabilityInsurance.detail.vehicleInfo.title'),
    },
    {
      rows: [
        {
          formatter: (value) => insuringPersonTypeOptions.find((option) => option.value === value)?.label ?? '',
          inputType: 'radioSelect',
          label: t('products:motorLiabilityInsurance.detail.insuringPerson.type.title'),
          options: insuringPersonTypeOptions,
          path: 'insuringPerson.type',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'insuringPerson.type'>,
        {
          inputType: 'text',
          label: t('products:motorLiabilityInsurance.detail.insuringPerson.name'),
          path: 'insuringPerson.name',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'insuringPerson.name'>,
      ],
      title: t('products:motorLiabilityInsurance.detail.insuringPerson.title'),
    },
    {
      rows: [
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          helperText: t('products:motorLiabilityInsurance.detail.vehicleOwner.dateOfBirth.helperText'),
          inputType: 'date',
          label: t('products:motorLiabilityInsurance.detail.vehicleOwner.dateOfBirth.label'),
          path: 'vehicleInfo.owner.dateOfBirth',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'vehicleInfo.owner.dateOfBirth'>,
        {
          helperText: t('products:motorLiabilityInsurance.detail.vehicleOwner.postalCode.helperText'),
          inputType: 'text',
          label: t('products:motorLiabilityInsurance.detail.vehicleOwner.postalCode.label'),
          path: 'vehicleInfo.owner.postalCode',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'vehicleInfo.owner.postalCode'>,
        {
          helperText: t('products:motorLiabilityInsurance.detail.vehicleOwner.yearOfLastCollision.helperText'),
          inputType: 'integer',
          label: t('products:motorLiabilityInsurance.detail.vehicleOwner.yearOfLastCollision.label'),
          path: 'vehicleInfo.owner.yearOfLastCollision',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'vehicleInfo.owner.yearOfLastCollision'>,
        {
          helperText: t('products:motorLiabilityInsurance.detail.vehicleOwner.numberOfRecentCollisions.helperText'),
          inputType: 'integer',
          label: t('products:motorLiabilityInsurance.detail.vehicleOwner.numberOfRecentCollisions.label'),
          path: 'vehicleInfo.owner.numberOfRecentCollisions',
        } as DetailRowType<MotorLiabilityInsuranceProductFragment, 'vehicleInfo.owner.numberOfRecentCollisions'>,
      ],
      title: t('products:motorLiabilityInsurance.detail.vehicleOwner.title'),
    },
  ];

  const [updateProduct] = useUpdateMotorLiabilityInsuranceProductMutation({
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['CalendarEventInstances', 'Notifications', 'NotificationsCount'],
  });

  const handleUpdate = async (value: MotorLiabilityInsuranceProductFragment) => {
    await updateProduct({ variables: { id, updatedProduct: mapMotorLiabilityInsuranceProductToInput(value) } });
  };

  return (
    <ProductDetail<MotorLiabilityInsuranceProductFragment>
      {...props}
      contactExpert={{
        avatarSrc: InsuranceAvatar,
      }}
      content={content}
      data={data?.detail}
      institution={data?.detail.institution ?? undefined}
      onUpdate={handleUpdate}
      title={t('products:motorLiabilityInsurance.detail.title')}
      type={FinancialProductType.MotorLiabilityInsurance}
    />
  );
};
